import { useEffect, useRef } from "react";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { asLanguageCode } from "./languages";

/**
 * Bridges the account-stored language preference with i18next.
 *
 * - Before auth, i18next has already picked a language from localStorage /
 *   the browser (see `./index.ts`), so the login screen is localized.
 * - Once authenticated, the account is the source of truth: if it carries a
 *   language, we switch to it (so the choice follows the user across devices).
 *   If the account has no preference yet, we persist the currently-active one,
 *   which is also what localizes the emails Echoes sends them.
 *
 * Renders nothing.
 */
export function LanguageSync() {
  const { isAuthenticated } = useConvexAuth();
  const { i18n } = useTranslation();
  const me = useQuery(api.users.me, isAuthenticated ? {} : "skip");
  const setLanguage = useMutation(api.users.setLanguage);
  const seededRef = useRef(false);

  useEffect(() => {
    if (!me) return; // still loading, or signed out

    if (!me.language) {
      if (seededRef.current) return;
      const current = asLanguageCode(i18n.language);
      if (current) {
        seededRef.current = true;
        void setLanguage({ language: current });
      }
      return;
    }

    if (me.language !== asLanguageCode(i18n.language)) {
      void i18n.changeLanguage(me.language);
    }
  }, [me, i18n, setLanguage]);

  return null;
}
